/**
 * 🚨 useProblemasRibbon — Hook de configuração do Ribbon para módulo Problemas (MASP)
 *
 * Layout:
 *   LEFT:    [+ Problema] [Arquivo ▾] [Assumir] [Criar Ação]
 *   CENTER:  [Ações ▾] [Lista] [Kanban] [Dashboard]
 *   RIGHT:   filtros + busca (via afterGroups slot)
 */

import { useMemo } from 'react';
import {
  MessageSquarePlus, UserCheck,
  ClipboardList,
  Shield, Eye, CheckCircle2, XCircle,
  List, Grid3X3, BarChart3,
  Folder, Save, SaveAll, FileText, Image as ImageIcon, TableProperties,
} from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';
import type { Issue } from '../types/issue';
import { isTransitionAllowed } from '../types/issue';

const NOOP = () => {};

export interface ProblemasRibbonDeps {
  selectedIssue: Issue | null;
  viewMode: 'list' | 'kanban';
  onViewModeChange: (mode: 'list' | 'kanban') => void;
  showDashboard: boolean;
  onToggleDashboard: () => void;
  hasActiveFilters: boolean;
  onNewIssue: () => void;
  onAssign: () => void;
  onCreateLinkedAction: () => void;
  onValidate: () => void;
  onMonitor: () => void;
  onCloseIssue: () => void;
  onCancelIssue: () => void;
  onSave: () => void;
  onSaveAsWf: () => void;
  onExportPDF: () => void;
  onExportPNG: () => void;
  onExportCSV: () => void;
  afterGroups?: React.ReactNode;
}

export function useProblemasRibbon(deps: ProblemasRibbonDeps): RibbonConfig {
  const {
    selectedIssue,
    viewMode,
    onViewModeChange,
    showDashboard,
    onToggleDashboard,
    onNewIssue,
    onAssign,
    onCreateLinkedAction,
    onValidate,
    onMonitor,
    onCloseIssue,
    onCancelIssue,
    onSave,
    onSaveAsWf,
    onExportPDF,
    onExportPNG,
    onExportCSV,
    afterGroups,
  } = deps;

  return useMemo<RibbonConfig>(() => {
    const hasSel = Boolean(selectedIssue);
    const status = selectedIssue?.status;
    const hasNoAssignee = hasSel && !selectedIssue?.assigned_to;

    const mode = selectedIssue?.mode ?? 'quick_correction';
    const canValidate = hasSel && status != null && isTransitionAllowed(mode, status, 'validation');
    const canMonitor = hasSel && status != null && isTransitionAllowed(mode, status, 'monitoring');
    const canClose = hasSel && status != null && isTransitionAllowed(mode, status, 'closed');
    const canReopen = hasSel && status != null && isTransitionAllowed(mode, status, 'open');

    return {
      moduleId: 'issues',
      groups: [
        // ─── Operação (LEFT) ──────────────────────
        {
          id: 'operacao',
          label: 'Operação',
          position: 'left',
          actions: [
            { id: 'new-issue', label: 'Problema', icon: MessageSquarePlus, onClick: onNewIssue },
            {
              id: 'arquivo',
              label: 'Arquivo',
              icon: Folder,
              onClick: NOOP,
              dropdown: [
                { id: 'save', label: 'Salvar', icon: Save, onClick: onSave },
                { id: 'save-as-wf', label: 'Salvar como .wf', icon: SaveAll, onClick: onSaveAsWf },
                { id: 'div-save', label: '', onClick: NOOP, divider: true },
                { id: 'export-pdf', label: 'Exportar PDF', icon: FileText, onClick: onExportPDF },
                { id: 'export-png', label: 'Exportar PNG', icon: ImageIcon, onClick: onExportPNG },
                { id: 'export-csv', label: 'Exportar Planilha (CSV)', icon: TableProperties, onClick: onExportCSV },
              ],
            },
            { id: 'assign', label: 'Assumir', icon: UserCheck, onClick: onAssign, hidden: !hasNoAssignee },
            { id: 'create-action', label: 'Criar Ação', icon: ClipboardList, onClick: onCreateLinkedAction, disabled: !hasSel },
          ],
        },
        // ─── Ações (CENTER) — dropdown ──────────
        {
          id: 'acoes',
          label: 'Ações',
          position: 'center',
          actions: [
            {
              id: 'control-actions',
              label: 'Ações',
              icon: Shield,
              onClick: NOOP,
              disabled: !hasSel,
              dropdown: [
                { id: 'validate', label: 'Validar', icon: Shield, onClick: onValidate, disabled: !canValidate },
                { id: 'monitor', label: 'Monitorar', icon: Eye, onClick: onMonitor, disabled: !canMonitor },
                { id: 'divider-1', label: '', onClick: NOOP, divider: true },
                { id: 'close', label: 'Encerrar', icon: CheckCircle2, onClick: onCloseIssue, disabled: !canClose },
                { id: 'cancel', label: 'Reabrir', icon: XCircle, onClick: onCancelIssue, disabled: !canReopen },
              ],
            },
          ],
        },
        // ─── Visual (CENTER) ───────────────────
        {
          id: 'visual',
          label: 'Visual',
          position: 'center',
          actions: [
            { id: 'view-list', label: 'Lista', icon: List, onClick: () => onViewModeChange('list'), active: viewMode === 'list' && !showDashboard },
            { id: 'view-kanban', label: 'Kanban', icon: Grid3X3, onClick: () => onViewModeChange('kanban'), active: viewMode === 'kanban' && !showDashboard },
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3, onClick: onToggleDashboard, active: showDashboard },
          ],
        },
      ],
      slots: {
        afterGroups,
      },
    };
  }, [
    selectedIssue,
    viewMode,
    onViewModeChange,
    showDashboard,
    onToggleDashboard,
    onNewIssue,
    onAssign,
    onCreateLinkedAction,
    onValidate,
    onMonitor,
    onCloseIssue,
    onCancelIssue,
    onSave,
    onSaveAsWf,
    onExportPDF,
    onExportPNG,
    onExportCSV,
    afterGroups,
  ]);
}
